import {Component, EventEmitter, inject, Inject, Input, OnInit, Output} from '@angular/core';
import {KeyValuePipe, NgForOf} from '@angular/common';
import {MatFormField} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {SharedModule} from '../shared.module';
import {SearchFilterModel} from '../../models/SearchFilter-model';
import {LocalStorageService} from '../../service/LocalStorageService';
import {normalizeMasterFilters} from '../constants/master-filter-fields';
import {formatFilterLabel} from '../format-filter-label';

@Component({
    selector: 'app-master-filters',
    standalone: true,
    imports: [KeyValuePipe, MatFormField, MatInput, MatOption, MatSelect, NgForOf, ReactiveFormsModule, SharedModule],
    templateUrl: './master-filters.component.html',
    styleUrl: './master-filters.component.css'
})
export class MasterFiltersComponent implements OnInit {
    @Input() displayedColumns: string[] = [];
    @Output() masterFiltersClosed = new EventEmitter<boolean>();
    @Output() selectedFilters = new EventEmitter<SearchFilterModel[]>();
    @Output() filterCount = new EventEmitter<number>();

    public allAvailableOptions: string[] = [];
    public searchFilterForm = new FormGroup({});
    private localStorageService = inject(LocalStorageService);

    constructor(@Inject('LOCAL_MASTER_KEY') private masterFilter: string) {}

    ngOnInit(): void {
        this.allAvailableOptions = [...new Set([...this.displayedColumns, 'versionName'])];
        const savedFilters = normalizeMasterFilters(this.localStorageService.getFilters(this.masterFilter))
            .filter(filter => this.allAvailableOptions.includes(filter.option));
        if (savedFilters.length) {
            this.searchFilterForm = this.localStorageService.createFormGroupFromFilters(savedFilters, this.allAvailableOptions);
        }
    }

    formatEnumKey(key: string): string {
        return formatFilterLabel(key);
    }

    addFormControl(): void {
        const keys = Object.keys(this.searchFilterForm.controls).map(key => Number(key.split('-')[1]) || 0);
        const controlKey = `control-${Math.max(0, ...keys) + 1}`;
        this.searchFilterForm.addControl(controlKey, new FormGroup({
            option: new FormControl(this.allAvailableOptions[0], Validators.required),
            value: new FormControl('', Validators.required)
        }));
    }

    private currentFilters(): SearchFilterModel[] {
        return normalizeMasterFilters(Object.keys(this.searchFilterForm.controls).map(key => {
            const value = this.searchFilterForm.get(key)?.value as SearchFilterModel;
            return {option: value.option, value: value.value};
        }));
    }

    clearFilters(): void {
        this.localStorageService.clearFilters(this.masterFilter);
        Object.keys(this.searchFilterForm.controls).forEach(key => this.searchFilterForm.removeControl(key));
        this.filterCount.emit(0);
        this.selectedFilters.emit([]);
    }

    closeModal(): void {
        this.masterFiltersClosed.emit(false);
    }

    onSubmit(): void {
        if (this.searchFilterForm.invalid || !this.hasFormControls()) return;
        const filters = this.currentFilters();
        if (filters.length !== Object.keys(this.searchFilterForm.controls).length) return;
        this.localStorageService.saveFilters(this.masterFilter, filters);
        this.selectedFilters.emit(filters);
        this.filterCount.emit(filters.length);
        this.closeModal();
    }

    deleteSearchBar(key: string): void {
        this.searchFilterForm.removeControl(key);
        const filters = this.currentFilters();
        this.localStorageService.saveFilters(this.masterFilter, filters);
        this.filterCount.emit(filters.length);
        this.selectedFilters.emit(filters);
    }

    hasFormControls(): boolean {
        return Object.keys(this.searchFilterForm.controls).length > 0;
    }
}
